import { useEffect, useRef, type ReactNode } from "react";
import { Navigate } from "react-router";
import { BffHttpError, BffUnauthenticatedError } from "@/shared/api/client";
import { BffIdentityIntegrityError } from "@/shared/api/identity";
import { signOut } from "@/shared/api/session";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { useIdentity } from "@/features/auth/hooks/use-session";
import {
  hasCompletedRepresentationPreview,
  representationPreviewEnabled,
} from "@/features/auth/lib/representation-preview";
import {
  AuthCard,
  AuthLayout,
  ErrorBanner,
  PrimaryButton,
  Wordmark,
} from "@/features/auth/components/AuthPrimitives";

export function AdvisorGate({ children }: { children: ReactNode }) {
  const { identity, loading, error, retry } = useIdentity();

  if (loading) return <AccessLoading />;
  if (error instanceof BffUnauthenticatedError) return null;
  if (isUnavailableAdvisor(error)) return <AccessSignOut reason="advisor_unavailable" />;
  if (error) return <AccessBootstrapError retry={retry} />;
  if (!identity) return <AccessBootstrapError retry={retry} />;

  if (identity.activeRepresentation?.kind === "financial-advisor") {
    return representationPreviewEnabled && !hasCompletedRepresentationPreview(identity.userId) ? (
      <Navigate to="/auth/representation" replace />
    ) : (
      children
    );
  }
  if (
    identity.alternativeRepresentations.some(
      (representation) => representation.kind === "financial-advisor",
    )
  ) {
    return <Navigate to="/auth/representation" replace />;
  }
  return <AccessSignOut reason="advisor_required" />;
}

function isUnavailableAdvisor(error: unknown): boolean {
  return (
    error instanceof BffIdentityIntegrityError ||
    (error instanceof BffHttpError && error.status === 404)
  );
}

function AccessLoading() {
  const { t } = useTranslation();
  return (
    <AuthLayout>
      <Wordmark />
      <p className="text-center text-md text-[var(--color-text-muted)]">
        {t("auth.access.checking")}
      </p>
    </AuthLayout>
  );
}

function AccessSignOut({ reason }: { reason: "advisor_required" | "advisor_unavailable" }) {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void signOut({ callbackUrl: `/auth/signin?reason=${reason}` });
  }, [reason]);
  return <AccessLoading />;
}

function AccessBootstrapError({ retry }: { retry: () => void }) {
  const { t } = useTranslation();
  return (
    <AuthLayout>
      <Wordmark />
      <AuthCard title={t("auth.access.errorTitle")} subtitle={t("auth.access.errorBody")}>
        <div className="flex flex-col gap-[12px]">
          <ErrorBanner>{t("auth.access.errorHint")}</ErrorBanner>
          <PrimaryButton type="button" onClick={retry}>
            {t("auth.access.retry")}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => void signOut({ callbackUrl: "/auth/signin" })}
            className="text-sm font-medium text-[var(--color-text-muted)] underline hover:no-underline"
          >
            {t("auth.access.signOut")}
          </button>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
